import styles from './shipping-guide.module.scss';
import Image from '@/components/common/SpiritoryImage';
import Link from 'next/link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faRightToBracket, faUserPlus } from '@fortawesome/free-solid-svg-icons';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import SearchInGuides from '@/components/common/searchInGuides';
import { useRouter } from 'next/router';

import numberOne from '@/public/images/shipping-guide/1.svg';
import numberTwo from '@/public/images/shipping-guide/2.svg';
import numberThree from '@/public/images/shipping-guide/3.svg';
import Head from 'next/head';
import Accordion from 'react-bootstrap/Accordion';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import useBackendUser from '@/hooks/useBackendUser';
import ShippingGuideStructuredData from '../components/structured_data/authenticating_structured_data';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

const ShippingGuide = () => {
  const { data: user } = useBackendUser();
  const { t } = useTranslation('shipping_guide');
  const { t: tOther } = useTranslation('other_translations');

  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>
          {t('shipping_guide_title')}
        </title>
        <meta
          name="description"
          content={t('shipping_guide_description')}
        />
        <meta
          name="keywords"
          content={t('shipping_guide_keywords')}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={t('shipping_guide_title')} />
        <meta property="og:description" content={t('shipping_guide_description')} />
        <meta property="og:image" content="https://spiritory.com/logo.png" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Spiritory" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('shipping_guide_title')} />
        <meta name="twitter:description" content={t('shipping_guide_description')} />
        <meta name="twitter:image" content="https://spiritory.com/logo.png" />
        <meta name="twitter:site" content="@spiritoryGlobal" />
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <ShippingGuideStructuredData />
      </Head>
      <div className={`${styles.mainContainer}`}>
        <div className={`bg-blue-gradient ${styles.mainSection}`}>
          <div className="d-flex align-items-center justify-content-between h-100">
            <div className={`d-flex align-items-start gap-3 ${styles.mainTextContainer}`}>
              <div className={styles.hideMobile}>
                <Image
                  src="/images/howItWorks/icon-shipping.svg"
                  alt="tag-icon"
                  width="57"
                  height="57"
                  style={{
                    objectFit: 'contain',
                    position: 'relative',
                    bottom: 5,
                  }}
                />
              </div>
              <div className={`d-flex flex-column align-items-start gap-2`}>
                <h1 className="h2 p-0 m-0 max-nine:tw-text-center">
                  {t('packagingAndShippingGuide')}
                </h1>
                <div>
                  <div className="d-flex align-items-center gap-2 max-nine:tw-text-center">
                    <FontAwesomeIcon icon={faCheck} className="text-primary max-nine:!tw-hidden" />
                    <p className="font-size-20 lh-23 p-0 m-0">
                      {t('prepareAndSellInfo')}
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-center max-nine:tw-flex-col gap-3 mt-3 max-nine:tw-w-auto max-nine:tw-m-auto">
                  <div
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-in');
                      }
                    }}
                    className={'max-nine:tw-w-[-webkit-fill-available]'}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-outline-primary !tw-px-[72px]`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faRightToBracket} />
                      </span>
                      {translationFallback('logInButtonText', t('logInButtonText'), 'Log In')}
                    </button>
                  </div>
                  <div
                    onClick={() => {
                      if (user) {
                        router.push('/dashboard');
                      } else {
                        router.push('/auth/sign-up');
                      }
                    }}
                  >
                    <button
                      className={`d-flex align-items-center gap-2 btn btn-s-primary !tw-px-[72px]`}
                      style={{ padding: '0.7rem 0' }}
                      type="button"
                    >
                      <span>
                        <FontAwesomeIcon icon={faUserPlus} />
                      </span>
                      {translationFallback('signUpButtonText', t('signUpButtonText'), 'Sign Up')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.mainImageContainer}>
              <Image
                src="/images/shipping-guide/section1-img.svg"
                alt="example"
                width={1000}
                height={1000}
                sizes="100vw"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>

        <div className={styles.packagingSection}>
          <div style={{ marginBottom: '3rem' }}>
            <h2 className="h2 text-center">
              {t('packagingTitle')}
            </h2>
          </div>
          <div className="d-flex flex-column gap-5">
            <div
              className={styles.packagingSectionChild}
              style={{
                backgroundImage: `url('/images/shipping-guide/section2-img1.svg')`,
                borderRadius: 20,
                padding: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div className={`d-flex align-items-center ${styles.packagingParent}`}>
                <div className={styles.packagingChild}>
                  <div style={{ width: 'fit-content' }}>
                    <h2 className="h3" style={{ fontWeight: '700', fontSize: '32px' }}>
                      {translationFallback(
                        'useCertifiedPackaging',
                        t('useCertifiedPackaging'),
                        '1. Use certified packaging'
                      )}
                    </h2>
                    <div className={`${styles.hrGold}`} style={{ width: '100%' }}></div>
                  </div>
                  <p>
                    {translationFallback(
                      'certifiedPackagingDescription',
                      t('certifiedPackagingDescription'),
                      'Please use certified packaging so that, in case of damage, the shipping company will refund you for your loss'
                    )}
                  </p>
                </div>
                <span className={`${styles.twoNumber} ${styles.packagingNum}`}>1</span>
              </div>
            </div>
            <div
              className={styles.packagingSectionChild}
              style={{
                backgroundImage: `url('/images/shipping-guide/section2-img2.svg')`,
                borderRadius: 20,
                padding: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className={`d-flex align-items-center ${styles.packagingParent} ${styles.packagingParentSpecial1}`}
              >
                <div className={styles.packagingChild}>
                  <div style={{ width: 'fit-content' }}>
                    <h2 className="h3" style={{ fontWeight: '700', fontSize: '32px' }}>
                      {translationFallback(
                        'packageBottleSafely',
                        t('packageBottleSafely'),
                        '2. Package your bottle safely'
                      )}
                    </h2>
                    <div className={`${styles.hrGold}`} style={{ width: '100%' }}></div>
                  </div>
                  <p>
                    {translationFallback(
                      'bottleSafetyDescription',
                      t('bottleSafetyDescription'),
                      'Please make sure that all sides of the bottle are protected. Take extra care of the neck and bottom areas and protect the box of the bottle well.'
                    )}
                  </p>
                </div>
                <span className={`${styles.twoNumber} ${styles.packagingNum}`}>2</span>
              </div>
            </div>
            <div
              className={styles.packagingSectionChild}
              style={{
                backgroundImage: `url('/images/shipping-guide/section2-img3.svg')`,
                borderRadius: 20,
                padding: '1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                className={`d-flex align-items-center ${styles.packagingParent} ${styles.packagingParentSpecial2}`}
              >
                <div className={styles.packagingChild}>
                  <div style={{ width: 'fit-content' }}>
                    <h2 className="h3" style={{ fontWeight: '700', fontSize: '32px' }}>
                      {translationFallback(
                        'labelFragile',
                        t('labelFragile'),
                        '3.Label the package with "Fragile"'
                      )}
                    </h2>
                    <div className={`${styles.hrGold}`} style={{ width: '100%' }}></div>
                  </div>
                  <p>
                    {translationFallback(
                      'fragileLabelDescription',
                      t('fragileLabelDescription'),
                      'This way, the risk can be minimized that your package will get damaged on its shipment.'
                    )}
                  </p>
                </div>
                <span className={`${styles.twoNumber} ${styles.packagingNum}`}>3</span>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.blueSection}>
          <div className="h2 mt-5 container d-flex justify-content-center">
            <h2 className="text-center">
              {translationFallback(
                'shippingDonts',
                t('shippingDonts'),
                "A list of things you shouldn't do when shipping:"
              )}
            </h2>
          </div>
          <div className="d-flex flex-wrap">
            <div className={styles.blueSectionCard}>
              <div>
                <Image
                  src="/images/shipping-guide/section3-img1.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '219px', height: '206px' }}
                />
                <Image
                  src="/images/shipping-guide/section3-icon.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                />
              </div>
              <p
                className="text-center"
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                }}
              >
                {translationFallback(
                  'doNotStickLabel',
                  t('doNotStickLabel'),
                  'Do not stick the label on the packaging of the whisky'
                )}
              </p>
            </div>
            <div className={styles.blueSectionCard}>
              <div>
                <Image
                  src="/images/shipping-guide/section3-img2.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '219px', height: '206px' }}
                />
                <Image
                  src="/images/shipping-guide/section3-icon.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                />
              </div>
              <p
                className="text-center"
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                }}
              >
                {translationFallback(
                  'adver',
                  tOther('adver'),
                  'Do not put any advertising material in your shipment'
                )}
              </p>
            </div>
            <div className={styles.blueSectionCard}>
              <div>
                <Image
                  src="/images/shipping-guide/section3-img3.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '219px', height: '206px' }}
                />
                <Image
                  src="/images/shipping-guide/section3-icon.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                />
              </div>
              <p
                className="text-center"
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                }}
              >
                {translationFallback(
                  'everyoriginal',
                  tOther('everyoriginal'),
                  'Ship all bottles with everything the original bottle came with: box, leaflets, etc.'
                )}
              </p>
            </div>
            <div className={styles.blueSectionCard}>
              <div>
                <Image
                  src="/images/shipping-guide/section3-img4.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                  style={{ width: '219px', height: '206px' }}
                />
                <Image
                  src="/images/shipping-guide/section3-icon.svg"
                  alt="example"
                  width={0}
                  height={0}
                  sizes="100vw"
                />
              </div>
              <p
                className="text-center"
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                }}
              >
                {translationFallback(
                  'personalItems',
                  tOther('personalItems'),
                  'Do not lay personal items in the shipment'
                )}
              </p>
            </div>
          </div>
        </div>

        <div
          id="shipping-section4 "
          className={`py-9 d-flex flex-column justify-content-center align-items-center`}
        >
          <h2 className="text-center mt-4 mt-md-0 mb-4 mb-md-7 h2">
            {t('shippingHeader')}
          </h2>
          <div
            className={`d-flex flex-md-row flex-column gap-5 align-items-center position-relative ${styles.shippingSection}`}
          >
            <div>
              <Image
                src="/images/shipping-guide/section4-img.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
            <div
              className={`p-md-0 p-2 ${styles.shippingSection4Text} position-relative`}
              style={{ maxWidth: 500 }}
            >
              <h2
                className="h3 mt-5"
                style={{
                  fontWeight: '700',
                  fontSize: '32px',
                  lineHeight: '40px',
                }}
              >
                {t('whenToShip')}
              </h2>
              <div className={`${styles.hrGold} mt-2`} style={{ width: '135px' }}></div>
              <p
                className="mt-2"
                style={{
                  fontWeight: '500',
                  fontSize: '20px',
                  lineHeight: '25px',
                }}
              >
                {translationFallback(
                  'shipWithin3Days',
                  t('shipWithin3Days'),
                  'Please ship your sold articles within 5 days of payment.'
                )}
              </p>
              <div className={`${styles.numberOne} ${styles.numberTwo}`}>
                <Image
                  src={numberOne}
                  height={15}
                  width={15}
                  style={{ width: '100%', height: '100%' }}
                  alt="number one"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          id="shipping-section5"
          className={`py-9 d-flex flex-md-row flex-column-reverse justify-content-between align-items-center gap-5 ${styles.shippingSection} ${styles.shippingSection2}`}
        >
          <div
            className={`${styles.shippingSection5TextContainer1} position-relative d-flex flex-column m-0`}
            style={{ flexShrink: 0 }}
          >
            <h3 className={`font-size-36 font-jakarta lh-40 ${styles.bold}`}>
              {translationFallback(
                'whatInfosProvided',
                t('whatInfosProvided'),
                '2.What infos have to be provided:'
              )}
            </h3>
            <div className={`${styles.hrGold}`}></div>
            <div className={`${styles.shippingSection5TextContainer2} p-0`}>
              <p className="font-size-20 lh-25 text-left p-0 m-0">
                {translationFallback(
                  'provideShipmentDetails',
                  t('provideShipmentDetails'),
                  'Once you are shipping your article, please provide us the details of your shipment and update the sale status to "shipped".'
                )}
              </p>
            </div>
            <div className={styles.numberTwo}>
              <Image
                src={numberTwo}
                height={15}
                width={15}
                style={{ width: '100%', height: '100%' }}
                alt="number two"
              />
            </div>
          </div>
          <div
            className={` ${styles.blueContainerImg} position-relative d-flex align-items-center justify-content-start`}
            style={{ marginRight: '0px' }}
          >
            <Image
              src="/images/shipping-guide/section5-img.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
              className={styles.shippingSectionImage}
            />
          </div>
        </div>

        <div
          id="shipping-section6"
          className={`py-9 container ${styles.fullHeightResponsive} d-flex flex-md-row flex-column justify-content-center align-items-center ${styles.shippingSection} ${styles.thirdSectionShipping} gap-5`}
        >
          <div className={`${styles.blueContainerImgSpecial} `}>
            <Image
              src="/images/shipping-guide/section6-img.svg"
              alt="example"
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              className={styles.img1}
            />
          </div>

          <div className={`${styles.containerText1} position-relative`}>
            <h3 className={`font-size-36 font-jakarta lh-40 ${styles.bold}`}>
              {translationFallback(
                'shippingCostsTitle',
                t('shippingCostsTitle'),
                '3.Shipping costs'
              )}
            </h3>
            <div className={`${styles.hrGold}`}></div>
            <div className="position-relative">
              <p className="font-size-20 lh-25">
                {translationFallback(
                  'shippingCostsDescription',
                  t('shippingCostsDescription'),
                  'Depending on where you will be shipping your package to, the buyer will be charged. You will have the option to set the shipping costs for each country.'
                )}
              </p>
            </div>
            <div className={`${styles.numberOne} ${styles.numberTwo}`}>
              <Image
                src={numberThree}
                height={15}
                width={15}
                style={{ width: '100%', height: '100%' }}
                alt="number three"
              />
            </div>
          </div>
        </div>

        <div
          className={`position-relative my-8 ${styles.lastSection}`}
          style={{
            backgroundColor: '#1C5CEB',
            color: '#FFFFFF',
            width: '95%',
            maxWidth: '100%',
            height: '378px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '20px',
          }}
        >
          <div
            className="position-absolute top-0 start-0 text-center"
            style={{ height: '100%', width: '100%' }}
          >
            <div
              className={`d-flex justify-content-between align-items-center ${styles.mainTextLastSection}`}
              style={{ height: '100%', overflow: 'hidden', width: '100%' }}
            >
              <div
                className="d-flex flex-column gap-2"
                style={{ marginLeft: '80px', marginTop: '0px' }}
              >
                <div>
                  <h2 className="h2 text-start">
                    {translationFallback('signUpToday', t('signUpToday'), 'Sign Up today')}
                  </h2>
                  <h3 className="text-start" style={{ marginBottom: '2rem' }}>
                    {translationFallback(
                      'qualityAssurance',
                      t('qualityAssurance'),
                      'With us you can be sure of the quality of the products you buy'
                    )}
                  </h3>
                </div>
                <div
                  onClick={() => {
                    if (user) {
                      return router.push('/condition-guide');
                    }
                    router.push('/auth/sign-up');
                  }}
                  className={`d-flex flex-row ${styles.buttonSection}`}
                >
                  <button className={`btn btn-light font-size-20 lh-25 ${styles.conditionButton}`}>
                    <Link
                      href="#"
                      style={{
                        textDecoration: 'none',
                        color: '#FFFFFF',
                        fontWeight: '700',
                      }}
                    >
                      {translationFallback(
                        'conditionGuideline',
                        t('conditionGuideline'),
                        'Condition guideline'
                      )}
                    </Link>
                  </button>
                  <button
                    onClick={() => {
                      if (user) {
                        return router.push('/authenticating');
                      }
                      router.push('/auth/sign-in');
                    }}
                    className={`btn btn-light font-size-20 lh-25 ${styles.whiteBtn} ${styles.customButton2}`}
                  >
                    <Link
                      href="#"
                      style={{
                        textDecoration: 'none',
                        color: '#1C5CEB',
                        fontWeight: '700',
                      }}
                    >
                      {translationFallback('authentication', t('authentication'), 'Authentication')}
                    </Link>
                  </button>
                </div>
              </div>
              <div
                className={`d-flex align-items-center justify-content-end ${styles.signUpImageContainer}`}
              >
                <Image
                  src="/images/shipping-guide/section7-img.svg"
                  alt="authenticating"
                  width={500}
                  height={500}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderTopRightRadius: 20,
                    borderBottomRightRadius: 20,
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div
          id="seller-Section10"
          className={` ${styles.textwhite} ${styles.greenContainer} px-0 py-6`}
          style={{ height: 'auto', backgroundColor: 'rgb(194, 173, 125)' }}
        >
          <div
            style={{ width: 'fit-content', margin: '0 auto' }}
            className={`container d-flex flex-column justify-content-center align-items-center mb-6 mt-2`}
          >
            <h2 className={`h2 text-center`}>
              {translationFallback(
                'specialCollectingTitle',
                t('specialCollectingTitle'),
                'What makes collecting with us special:'
              )}
            </h2>
            <div
              className="mt-2"
              style={{ width: '70%', height: 4, backgroundColor: '#C2AD7D' }}
            ></div>
          </div>
          <div
            className={`d-flex justify-content-center align-items-center ${styles.allCards}`}
            style={{ gap: '32px' }}
          >
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'transparencyOnFees',
                  t('transparencyOnFees'),
                  '100% transparency on all fees'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri2.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'simpleQuickAuthentication',
                  t('simpleQuickAuthentication'),
                  'Simple and quick authentication'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri3.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-2">
                {translationFallback(
                  'safeSecurePayoutProcess',
                  t('safeSecurePayoutProcess'),
                  'Safe and secure payout process'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri4.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'inDepthPricingDataSalesStatistics',
                  t('inDepthPricingDataSalesStatistics'),
                  'In depth pricing data and sales statistics'
                )}
              </p>
            </div>
            <div
              className="col d-flex flex-column justify-content-center align-items-center"
              style={{
                background: '#FFFFFF1A',
                borderRadius: '20px',
                width: '359px',
                height: '193px',
              }}
            >
              <Image
                src="/images/sell-guide/gri1.svg"
                alt="example"
                width={0}
                height={0}
                sizes="100vw"
                style={{
                  width: '80px',
                  height: 'auto',
                  objectFit: 'contain',
                  margin: '22px',
                }}
              />
              <p className="mx-4 text-center">
                {translationFallback(
                  'sellBuySecondsNoHassle',
                  t('sellBuySecondsNoHassle'),
                  'Sell and buy in just a few seconds, no hassle'
                )}
              </p>
            </div>
          </div>
        </div>

        <SearchInGuides
          becomeTitle={translationFallback(
            'becomeBuyer',
            t('becomeBuyer'),
            'Become a Buyer right now'
          )}
          becomeCallToAct={translationFallback(
            'findDreamBottle',
            t('findDreamBottle'),
            'Find your dream bottle today'
          )}
          userId={user?.id ?? null}
        />

        <div id="shiping-section10" className="container px-2 px-sm-8 py-6 py-sm-4">
          <div className={`h3 ${styles.bold}`}>
            {translationFallback('faqTitle', t('faqTitle'), 'Frequently asked questions')}
            {translationFallback('faqTitle', t('faqTitle'), 'Frequently asked questions')}
          </div>

          <Accordion defaultActiveKey="0" className={'my-4'}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq1_q',
                    t('faqs.faq1_q'),
                    'Can I change the delivery address after placing an order?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                {translationFallback(
                  'faqs.faq1_a',
                  t('faqs.faq1_a'),
                  'Yes, you can change your delivery address as long as the order has not yet been shipped. To do this, please contact our customer support team immediately with the updated address. We will make every effort to accommodate the change. However, once the order has been shipped, no further modifications are possible.'
                )}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq2_q',
                    t('faqs.faq2_q'),
                    'What should I do if my bottle arrives damaged?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq2_a',
                      t('faqs.faq2_a'),
                      '"If your ordered bottle doesn’t arrive or is damaged, we’ll do our best to connect you with another seller on our platform offering the same bottle. This allows us to send a replacement as quickly as possible. Please note that the new seller will need time to pack and ship the bottle, which might slightly extend delivery time.<br>' +
                        'Our goal is to provide a reliable and secure platform where quality and customer satisfaction come first.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq3_q',
                    t('faqs.faq3_q'),
                    'What are the return policies?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq3_a',
                      t('faqs.faq3_a'),
                      'Returns are accepted for unopened bottles in their original condition purchased from commercial sellers within 14 days of receipt. Returns are not available for opened products or purchases from private sellers. If a bottle arrives damaged, please contact our support team for further assistance.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>
                <b>
                  {translationFallback('faqs.faq4_q', t('faqs.faq4_q'), 'Can I retun a bottle?')}
                  {translationFallback('faqs.faq4_q', t('faqs.faq4_q'), 'Can I retun a bottle?')}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq4_a',
                      t('faqs.faq4_a'),
                      'Yes, you can return unopened bottles in their original packaging purchased from commercial sellers within 14 days. Returns are not permitted for bottles that have been opened or altered'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="4">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq5_q',
                    t('faqs.faq5_q'),
                    'When can I expect my delivery?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq5_a',
                      t('faqs.faq5_a'),
                      'After authentication, the seller is notified to ship the product. Delivery times depend on the locations of both the seller and the buyer. For shipments within the EU, you can generally expect to receive your order within 5–12 business days.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="5">
              <Accordion.Header>
                <b>
                  {translationFallback('faqs.faq6_q', t('faqs.faq6_q'), 'Can I cancel my order?')}
                  {translationFallback('faqs.faq6_q', t('faqs.faq6_q'), 'Can I cancel my order?')}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq6_a',
                      t('faqs.faq6_a'),
                      'Orders from private sellers are binding and cannot be canceled. For commercial sellers, EU consumers may cancel their orders within 14 days of receipt, provided the bottle remains unopened and in its original condition.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq7_q',
                    t('faqs.faq7_q'),
                    'What do I need about the shipping '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq7_a',
                      t('faqs.faq7_a'),
                      'Use certified packaging to ensure safe delivery. Protect sensitive areas of the bottle and mark the package as "Fragile." Include the original packaging and any accessories. Always upload the tracking number once the shipment is sent.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="7">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq8_q',
                    t('faqs.faq8_q'),
                    'How much is the shipping cost for the buyers? '
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq8_a',
                      t('faqs.faq8_a'),
                      'Shipping costs are included in the purchase price displayed on the platform. Buyers will not face additional shipping fees.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="8">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq9_q',
                    t('faqs.faq9_q'),
                    "Why hasn't my seller shipped yet?"
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq9_a',
                      t('faqs.faq9_a'),
                      "Sellers are required to ship within 3–7 business days after the bottle passes authentication. Occasionally, delays can occur due to the seller's location or availability. If the shipping time exceeds this period significantly, please reach out to our customer support team for assistance. We will investigate the status of your order and provide updates."
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="9">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq10_q',
                    t('faqs.faq10_q'),
                    'How much time I have to return a bottle?'
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq10_a',
                      t('faqs.faq10_a'),
                      'For purchases from commercial sellers, you have 14 days from the date of receipt to request a return, provided the bottle remains unopened and in its original condition'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="10">
              <Accordion.Header>
                <b>
                  {translationFallback(
                    'faqs.faq11_q',
                    t('faqs.faq11_q'),
                    "Why hasn't my seller shipped yet?"
                  )}
                </b>
              </Accordion.Header>

              <Accordion.Body>
                <div
                  dangerouslySetInnerHTML={{
                    __html: translationFallback(
                      'faqs.faq11_a',
                      t('faqs.faq11_a'),
                      'Shipping costs are included in the purchase price displayed on the platform. Buyers will not face additional shipping fees.'
                    ),
                  }}
                />
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
      </div>
    </>
  );
};

export const getStaticProps = getBasicStaticSideProps([
  'common',
  'shipping_guide',
  'index',
  'other_translations',
]);

export default ShippingGuide;
